import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PropertyPanel = ({ selectedComponent, onUpdateComponent }) => {
  const [activeTab, setActiveTab] = useState('style');

  const tabs = [
    { id: 'style', label: 'Style', icon: 'Palette' },
    { id: 'content', label: 'Content', icon: 'Type' },
    { id: 'layout', label: 'Layout', icon: 'LayoutGrid' },
    { id: 'advanced', label: 'Advanced', icon: 'Settings' }
  ];

  const colorOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'accent', label: 'Accent' },
    { value: 'muted', label: 'Muted' },
    { value: 'destructive', label: 'Destructive' }
  ];

  const sizeOptions = [
    { value: 'sm', label: 'Small' },
    { value: 'default', label: 'Default' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra Large' }
  ];

  const alignmentOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
    { value: 'justify', label: 'Justify' }
  ];

  if (!selectedComponent) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-card">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mb-4">
          <Icon name="MousePointerClick" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-2">
          No Component Selected
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Select a component from the canvas to view and edit its properties
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={selectedComponent?.icon} size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base text-foreground truncate">
              {selectedComponent?.name}
            </h3>
            <p className="text-xs text-muted-foreground truncate">
              {selectedComponent?.description}
            </p>
          </div>
        </div>

        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md transition-all duration-250 ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="text-xs font-medium hidden md:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'style' && (
          <>
            <Select
              label="Color Scheme"
              options={colorOptions}
              value="primary"
              onChange={() => {}}
              className="mb-4"
            />
            <Select
              label="Size"
              options={sizeOptions}
              value="default"
              onChange={() => {}}
              className="mb-4"
            />
            <Input
              label="Border Radius"
              type="text"
              placeholder="12px"
              className="mb-4"
            />
            <Input
              label="Shadow"
              type="text"
              placeholder="0 4px 6px rgba(0,0,0,0.1)"
              className="mb-4"
            />
          </>
        )}

        {activeTab === 'content' && (
          <>
            <Input
              label="Label Text"
              type="text"
              placeholder="Enter label"
              className="mb-4"
            />
            <Input
              label="Placeholder"
              type="text"
              placeholder="Enter placeholder"
              className="mb-4"
            />
            <Select
              label="Text Alignment"
              options={alignmentOptions}
              value="left"
              onChange={() => {}}
              className="mb-4"
            />
          </>
        )}

        {activeTab === 'layout' && (
          <>
            <Input
              label="Width"
              type="text"
              placeholder="100%"
              className="mb-4"
            />
            <Input
              label="Height"
              type="text"
              placeholder="auto"
              className="mb-4"
            />
            <Input
              label="Margin"
              type="text"
              placeholder="16px"
              className="mb-4"
            />
            <Input
              label="Padding"
              type="text"
              placeholder="16px"
              className="mb-4"
            />
          </>
        )}

        {activeTab === 'advanced' && (
          <>
            <Input
              label="Custom CSS Class"
              type="text"
              placeholder="custom-class"
              className="mb-4"
            />
            <Input
              label="ID Attribute"
              type="text"
              placeholder="component-id"
              className="mb-4"
            />
            <Input
              label="Data Attribute"
              type="text"
              placeholder="data-value"
              className="mb-4"
            />
          </>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <button
          onClick={() => onUpdateComponent(selectedComponent)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-250 caption font-semibold"
        >
          <Icon name="Save" size={18} />
          <span>Apply Changes</span>
        </button>
      </div>
    </div>
  );
};

export default PropertyPanel;